import type { Job } from "providers/HostedScrapingProvider";
import { COLOR_OK, COLOR_STARTED, COLOR_ERROR, COLOR_GRAY, COLOR_PAUSED } from "components/hosted-scraping/ProjectColors";
import { ReactComponent as ProjectJobsCheckmark } from "assets/icons/project-jobs-checkmark.svg";
import { ReactComponent as ProjectJobsPlayIcon } from "assets/icons/project-jobs-play-icon.svg";
import { ReactComponent as ProjectJobsXIcon } from "assets/icons/project-jobs-x-icon.svg";
import { ReactComponent as ProjectJobsFailed } from "assets/icons/project-jobs-failed.svg";
import { LinkText } from "components/LinkText";
import { LittleProgressBar, PROGRESS_BAR_COLOR_OK } from "components/hosted-scraping/project-summary/ProjectSummaryComponents";
import { PlaceHolderJobToMarkQueuedJob } from "components/hosted-scraping/job-list/utils";

type FrontendJobStatus =
  | 'queued'
  | 'in_progress'
  | 'cancelling'
  | 'finalizing'
  | 'completed'
  | 'partially_completed'
  | 'failed'
  | 'cancelled'

export const frontendJobStatusForJob = (job: Job | PlaceHolderJobToMarkQueuedJob): FrontendJobStatus => {
  const { status, inProgressTasks } = job;
  const aggregating = status === 'running' && inProgressTasks === 0;
  if (job.status === 'running') {
    return aggregating ? 'finalizing' : 'in_progress';
  } else if (job.status === 'cancelling') {
    return 'cancelling';
  } else if (job.status === 'cancelled') {
    return 'cancelled';
  } else if (job.status === 'error') {
    return 'failed';
  } else if (job.status === 'done') {
    if (job.successfulTasks > 0 && job.failedTasks === 0) {
      return 'completed';
    } else if (job.successfulTasks > 0 && job.failedTasks > 0) {
      return 'partially_completed';
    } else if (job.successfulTasks === 0 && job.failedTasks === 0) {
      return 'completed';
    } else {
      return 'failed';
    }
  } else if (job.status === 'queued') {
    return 'queued';
  }

  throw new Error('unreachable because an unknown job status');
}

type IJobStatusProp = {
  job: Job | PlaceHolderJobToMarkQueuedJob;
  cancelJobButtonClicked: () => void;
  progressPercentage: number | undefined;
  timeEstimationLabel: string | undefined;
}

export const JobStatus = ({job, cancelJobButtonClicked, progressPercentage, timeEstimationLabel}: IJobStatusProp) => {
  const { successfulTasks, failedTasks, inProgressTasks } = job;
  const frontendStatus = frontendJobStatusForJob(job);

  const { icon: Icon, color, text } = (() => {
    switch (frontendStatus) {
      case 'queued': return { icon: ProjectJobsPlayIcon, color: COLOR_GRAY, text: 'Queued' };
      case 'in_progress': return { icon: ProjectJobsPlayIcon, color: COLOR_STARTED, text: 'In progress' };
      case 'finalizing': return { icon: ProjectJobsPlayIcon, color: COLOR_STARTED, text: 'Finalizing' };
      case 'cancelling': return { icon: ProjectJobsXIcon, color: COLOR_GRAY, text: 'Cancelling and wrapping up' };
      case 'cancelled': return { icon: ProjectJobsXIcon, color: COLOR_GRAY, text: 'Cancelled' };
      case 'completed': return { icon: ProjectJobsCheckmark, color: COLOR_OK, text: 'Completed' };
      case 'partially_completed': return { icon: ProjectJobsCheckmark, color: COLOR_PAUSED, text: 'Partially completed' };
      case 'failed': return { icon: ProjectJobsFailed, color: COLOR_ERROR, text: 'Failed' };
    }
  })();

  let inProgressMessage = null;
  const allTasks = successfulTasks + failedTasks + inProgressTasks;
  const finishedTasks = successfulTasks + failedTasks;
  if (frontendStatus === 'in_progress') {
    inProgressMessage = `${finishedTasks} / ${allTasks} URLs completed`;
  } else if ((frontendStatus === 'finalizing' || frontendStatus === 'cancelling') && job.aggregationProgress) {
    inProgressMessage = `${job.aggregationProgress.finished} / ${job.aggregationProgress.total} processed`;
  }

  const progressBar = (frontendStatus === 'in_progress' || frontendStatus === 'finalizing' || frontendStatus === 'cancelling')
    ? ( <>
    { timeEstimationLabel && <div className="text-xs">{timeEstimationLabel}</div> }
    { progressPercentage !== undefined && <div className="mt-1">
      <LittleProgressBar progress={progressPercentage} color={PROGRESS_BAR_COLOR_OK} />
    </div>}
    </>)
    : null;

  const showCancel = frontendStatus === 'in_progress' || frontendStatus === 'queued';

  return (
    <div className="flex flex-row flex-wrap items-center gap-x-1">
      <div className="flex gap-1 items-center min-w-0">
        <Icon stroke={color} fill={color} className="flex-none" />
        <span className="break-words">{text}</span>
      </div>
      {showCancel && (
        <div className="flex gap-1 items-center flex-none">
          <span>-</span>
          <button onClick={cancelJobButtonClicked}>
            <LinkText>cancel</LinkText>
          </button>
        </div>
      )}
      {(frontendStatus === 'in_progress' || frontendStatus === 'finalizing' || frontendStatus === 'cancelling') && (
        <div className="w-full">
          {inProgressMessage && <div className="text-xs">{inProgressMessage}</div>}
          {progressBar}
        </div>
      )}
    </div>
  );
}
